import React, { FunctionComponent, useEffect, useState } from 'react';
import { client } from '@trello/config';
import { defaultRouter } from 'app/common/router';
import { useFeatureFlag } from '@trello/feature-flag-client';
import { Spinner } from 'app/src/components/Spinner';
import styles from './MinimumVersionUpdater.less';
import Cookies from 'js-cookie';
import { isDesktop } from '@trello/browser';

interface ClientChecks {
  clientHead?: string;
  clientVersion?: number;
  isDesktopClient?: boolean;
}

export const useMinimumRequiredVersionUpdater = (
  clientChecks: ClientChecks = {},
) => {
  // Passing this as params helps with testing.
  // There's currently a limitation on spying on named exports,
  // and passing the client.head|version instead of overriding
  // globally in tests is safer
  const {
    clientHead = client.head,
    clientVersion = client.version,
    isDesktopClient = isDesktop(),
  } = clientChecks;
  const minimumRequiredVersion = useFeatureFlag(
    'web.minimum-required-version',
    0,
  );

  const [isUpdating, setIsUpdating] = useState(false);

  // If our client is below the minimum required version, and they
  // are on a 'build' head and not using a custom version cookie,
  // we will subscribe to the next navigation and trigger a full page
  // refresh.
  const willUpdateOnNavigation =
    !Cookies.get('head') &&
    clientHead === 'build' &&
    !isDesktopClient &&
    clientVersion < minimumRequiredVersion;

  useEffect(
    () => {
      if (willUpdateOnNavigation) {
        const currentRoutePath = defaultRouter.getRoute().routePath;
        const unsubscribe = defaultRouter.subscribe(({ routePath }) => {
          if (currentRoutePath !== routePath) {
            setIsUpdating(true);
            window.location.reload();
          }
        });
        return unsubscribe;
      }
    },
    [willUpdateOnNavigation],
  );

  return { isUpdating, willUpdateOnNavigation };
};

export const MinimumVersionUpdater: FunctionComponent = ({ children }) => {
  const { isUpdating } = useMinimumRequiredVersionUpdater();

  // If we have started refreshing the page, we don't want to render the app anymore
  // as it leads to a successful transition followed by the full page refresh
  if (isUpdating) {
    return <Spinner centered wrapperClassName={styles.spinner} />;
  }

  return <>{children}</>;
};
